import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { useVariables } from '../../../../variables'
import { IApiDataType } from '../../../types'

interface IDataType {
  getDisputeReasons: {
    nodes: {
      reason: string
      totalCount: string
      totalOrderPercent: string
      totalAmount: string
    }[]
  }
}

const query = gql`
  query disputeReasons($iEndDate: Date!, $iStartDate: Date!, $iFilter: JSON!) {
    getDisputeReasons(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        reason
        totalCount
        totalOrderPercent
        totalAmount
      }
    }
  }
`

export const bbbDisputeReasonConfigs = {
  reason: 'string',
  totalCount: 'number',
  totalOrderPercent: 'percent',
  totalAmount: 'price',
} as const

const YESTERDAY = moment().subtract(1, 'day')

const useBbbDisputeReason = () => {
  const { variables } = useVariables()
  const { startDate, endDate: filterEndDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const endDate = useMemo(
    () =>
      moment(filterEndDate, 'YYYY-MM-DD').isAfter(YESTERDAY)
        ? YESTERDAY.format('YYYY-MM-DD')
        : filterEndDate,
    [filterEndDate],
  )
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: variables.corporateGroup
        ? {
            location_group_ids: groupFilter?.ids,
          }
        : {
            location_ids: groupFilter?.ids,
          },
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const disputeReason = data?.getDisputeReasons.nodes

      if (!disputeReason) return null

      return disputeReason.map((d) => ({
        ...d,
        id: d.reason,
        parentId: 'root',
        totalCount: parseFloat(d.totalCount),
        totalOrderPercent: parseFloat(d.totalOrderPercent),
        totalAmount: parseFloat(d.totalAmount),
      }))
    }, [data]),
    loading,
  }
}

export default useBbbDisputeReason
